import UserRolesTableField from 'account/components/userManagement/tenants/UserRolesTableField'
import { listUsers } from 'account/components/userManagement/users/new-actions'
import { usersSelector } from 'account/components/userManagement/users/selectors'
import { generateApiErrorPayload, getDomainId } from 'api-client/helpers'
import { setDefaultVolumeTypeTenant } from 'app/plugins/openstack/components/storage/volume-types/actions'
import VolumeTypesPicklist from 'app/plugins/openstack/components/storage/volume-types/VolumeTypesPicklist'
import Progress from 'core/components/progress/Progress'
import PicklistField from 'core/components/validatedForm/DropdownField'
import FormFieldSection from 'core/components/validatedForm/FormFieldSection'
import TextField from 'core/components/validatedForm/TextField'
import ModalForm from 'core/elements/modal/ModalForm'
import useListAction from 'core/hooks/useListAction'
import useParams from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { Route } from 'core/plugins/route'
import { isSystemAdminUser } from 'core/utils/helpers'
import { routeManager } from 'core/utils/routes'
import { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import useReactRouter from 'use-react-router'
import DomainNameTextField from '../domains/DomainNameTextField'
import DomainPickList from '../domains/DomainPickList'
import { createTenant, listUserTenants } from './new-actions'
import VmStorageTypePicklist, { VmStorageOptions } from './VmStorageTypePicklist'

interface Props {
  addRoute: Route
  reload: (force?: boolean, reset?: boolean) => void
  domainId: string
}

export default function CreateTenantModal({ addRoute, reload, domainId }: Props) {
  const { history } = useReactRouter()

  const defaultParams = {
    name: '',
    description: '',
    volumeType: '',
    roleAssignments: {},
    domainId: domainId,
    storageType: VmStorageOptions.ephemeral_and_volume,
  }
  const { reload: reloadTenants } = useListAction(listUserTenants)
  const [volumeTypeError, setVolumeTypeError] = useState(null)
  const { params, getParamsUpdater, setParams } = useParams(defaultParams)
  const { update, updating, error, reset } = useUpdateAction(createTenant)
  const {
    update: setDefaultFn,
    updating: updatingTenantVolumeType,
    reset: resetTenantVolumeType,
  } = useUpdateAction(setDefaultVolumeTypeTenant)
  const { loading, reload: reloadUsers } = useListAction(listUsers, {
    params: {
      domainId: params.domainId,
    },
  })
  const users = useSelector(usersSelector)

  useEffect(() => {
    reloadUsers(true)
  }, [params?.domainId])

  useEffect(() => {
    setParams({ ...defaultParams, domainId })
  }, [domainId])

  const submitForm = useCallback(async () => {
    const vmStorageTypeTag = `vm_storage_type=${params.storageType}`
    const body = {
      name: params.name,
      description: params.description,
      roleAssignments: params.roleAssignments,
      domain_id: params.domainId,
      tags: [vmStorageTypeTag],
    }
    const { success, response } = await update(body)
    if (success) {
      if (params.volumeType) {
        const { success: volumeTypeSuccess, error: volumeTypeError } = await setDefaultFn({
          id: response?.id,
          body: { volume_type: params.volumeType },
        })
        if (volumeTypeSuccess) {
          resetTenantVolumeType()
        } else {
          setVolumeTypeError(
            generateApiErrorPayload(volumeTypeError, '', 'Error setting default volume type'),
          )
          return
        }
      }
      // reload tenants if the created tenant is in the same domain
      if (getDomainId() === params.domainId) {
        reloadTenants(true, true)
      }
      reload(true, true)
      handleClose()
    }
  }, [params])

  const volumeTypeFilterFn = useCallback(
    (options) => options.filter((option) => option?.label && option?.label !== '__DEFAULT__'),
    [],
  )

  const handleClose = () => {
    setVolumeTypeError(null)
    setParams(defaultParams)
    reset()
    history.push(routeManager.routes.userManagement.tenants.path())
  }

  return (
    <ModalForm
      route={addRoute}
      title={`Create Tenant`}
      onSubmit={submitForm}
      onClose={handleClose}
      submitting={updating || updatingTenantVolumeType}
      error={error || volumeTypeError}
      initialValues={defaultParams}
      submitTitle={`Create Tenant`}
    >
      <>
        <FormFieldSection title="Tenant Settings">
          <TextField
            id="name"
            label="Name"
            onChange={getParamsUpdater('name')}
            value={params.name}
            required
          />
          <TextField
            id="description"
            label="Description"
            onChange={getParamsUpdater('description')}
            value={params.description}
          />
          {isSystemAdminUser() ? (
            <DomainPickList onChange={getParamsUpdater('domainId')} value={params.domainId} />
          ) : (
            <DomainNameTextField domainId={params.domainId} isReadOnly />
          )}
          <PicklistField
            DropdownComponent={VolumeTypesPicklist}
            id="volumeType"
            onChange={getParamsUpdater('volumeType')}
            value={params.volumeType}
            label="Default Volume Type"
            filterFn={volumeTypeFilterFn}
          />
          <VmStorageTypePicklist
            value={params.storageType}
            onChange={getParamsUpdater('storageType')}
          />
        </FormFieldSection>
        <FormFieldSection title="User Assignment">
          <Progress renderContentOnMount={!loading} loading={loading} message={'Loading Users...'}>
            <UserRolesTableField
              // validations={userRolesValidations}
              id="roleAssignments"
              users={users}
              onChange={getParamsUpdater('roleAssignments')}
            />
          </Progress>
        </FormFieldSection>
      </>
    </ModalForm>
  )
}
